/**
 * Validador local da condição — **só para UX**, antes de salvar.
 *
 * Espelha `ValidadorCondicao` (T8a): mesmos códigos de erro e mesmo `caminho`
 * (`$.todos[1].algum[0].op`), para que o erro local caia na mesma linha em que cairia o 400 do
 * backend. A seção `casosValidacao` da fixture de paridade prova a equivalência.
 *
 * Nunca lança por conteúdo inválido: devolve um erro por problema encontrado.
 */
import {
  CampoEsquema,
  CodigoErroCondicao,
  ErroCondicao,
  Fonte,
  Operador,
  OPERADORES,
  PROFUNDIDADE_MAXIMA,
  TipoValor,
  antesDe,
  operadorExigeValor,
  tipoValorDoCampo,
} from './tipos'
import { CondicaoCatalogo, operadoresPara, variavelPorCodigo, sinalPorCodigo } from './catalogo'
import { normalizar } from './valores'

const CHAVES_ATOMICAS = ['fonte', 'ref', 'op', 'valor']
const FONTES: Fonte[] = ['campo', 'variavel', 'sinal']

export type EsquemaValidacao = {
  /** Fontes aceitas neste uso: `['campo','variavel']` na exibição. */
  fontesPermitidas: Fonte[]
  /** Campos do formulário, por código. */
  campos: CampoEsquema[]
  /** Campo dono da condição de exibição: limita as referências aos campos anteriores. */
  campoAtual?: CampoEsquema | null
  catalogo: CondicaoCatalogo
}

export const esquemaParaExibicao = (
  campos: CampoEsquema[],
  campoAtual: CampoEsquema | null | undefined,
  catalogo: CondicaoCatalogo
): EsquemaValidacao => ({
  fontesPermitidas: ['campo', 'variavel'],
  campos,
  campoAtual: campoAtual ?? null,
  catalogo,
})

export const esquemaParaRegra = (
  campos: CampoEsquema[],
  catalogo: CondicaoCatalogo
): EsquemaValidacao => ({
  fontesPermitidas: ['campo', 'variavel', 'sinal'],
  campos,
  campoAtual: null,
  catalogo,
})

const erro = (caminho: string, codigo: CodigoErroCondicao, mensagem: string): ErroCondicao => ({
  caminho,
  codigo,
  mensagem,
})

const ehObjeto = (no: unknown): no is Record<string, unknown> =>
  typeof no === 'object' && no !== null && !Array.isArray(no)

const chaves = (no: Record<string, unknown>): string[] => Object.keys(no)

const descreve = (no: unknown): string => {
  if (no === undefined || no === null) return '(ausente)'
  return typeof no === 'string' ? `"${no}"` : JSON.stringify(no)
}

/** Valida a condição inteira. Lista vazia = condição válida. */
export const validarCondicaoLocal = (raiz: unknown, esquema: EsquemaValidacao): ErroCondicao[] => {
  if (raiz === undefined || raiz === null) {
    return [erro('$', 'JSON_INVALIDO', 'Condição vazia')]
  }
  if (ehObjeto(raiz) && !ehGrupoBruto(raiz)) {
    return [
      erro('$', 'ESTRUTURA_INVALIDA', 'A raiz da condição precisa ser um grupo "todos" ou "algum"'),
    ]
  }
  const erros: ErroCondicao[] = []
  validarNo(raiz, '$', 1, esquema, erros)
  return erros
}

const ehGrupoBruto = (no: Record<string, unknown>): boolean =>
  Object.prototype.hasOwnProperty.call(no, 'todos') ||
  Object.prototype.hasOwnProperty.call(no, 'algum')

const validarNo = (
  no: unknown,
  caminho: string,
  profundidade: number,
  esquema: EsquemaValidacao,
  erros: ErroCondicao[]
): void => {
  if (!ehObjeto(no)) {
    erros.push(erro(caminho, 'ESTRUTURA_INVALIDA', 'Cada nó da condição precisa ser um objeto'))
    return
  }
  const presentes = chaves(no)
  const temTodos = presentes.includes('todos')
  const temAlgum = presentes.includes('algum')

  if (temTodos && temAlgum) {
    erros.push(
      erro(caminho, 'ESTRUTURA_INVALIDA', 'Um nó não pode ter "todos" e "algum" ao mesmo tempo')
    )
    return
  }
  if (temTodos || temAlgum) {
    validarGrupo(no, caminho, profundidade, esquema, erros, temTodos ? 'todos' : 'algum', presentes)
    return
  }
  validarAtomica(no, caminho, esquema, erros, presentes)
}

const validarGrupo = (
  no: Record<string, unknown>,
  caminho: string,
  profundidade: number,
  esquema: EsquemaValidacao,
  erros: ErroCondicao[],
  tipo: 'todos' | 'algum',
  presentes: string[]
): void => {
  const caminhoGrupo = `${caminho}.${tipo}`
  if (presentes.length > 1) {
    const extras = presentes.filter((chave) => chave !== tipo).join(', ')
    erros.push(
      erro(caminho, 'ESTRUTURA_INVALIDA', `Grupo "${tipo}" não aceita outras chaves: ${extras}`)
    )
    return
  }
  if (profundidade > PROFUNDIDADE_MAXIMA) {
    erros.push(
      erro(
        caminhoGrupo,
        'PROFUNDIDADE_EXCEDIDA',
        `Profundidade máxima de ${PROFUNDIDADE_MAXIMA} grupos aninhados excedida`
      )
    )
    return
  }
  const itens = no[tipo]
  if (!Array.isArray(itens)) {
    erros.push(
      erro(caminhoGrupo, 'ESTRUTURA_INVALIDA', `Grupo "${tipo}" precisa ser uma lista de condições`)
    )
    return
  }
  if (itens.length === 0) {
    erros.push(erro(caminhoGrupo, 'GRUPO_VAZIO', `Grupo "${tipo}" não pode ser vazio`))
    return
  }
  itens.forEach((item, indice) =>
    validarNo(item, `${caminhoGrupo}[${indice}]`, profundidade + 1, esquema, erros)
  )
}

type Referencia = {
  tipo: TipoValor
  lista: boolean
  dominio: string[]
}

const validarAtomica = (
  no: Record<string, unknown>,
  caminho: string,
  esquema: EsquemaValidacao,
  erros: ErroCondicao[],
  presentes: string[]
): void => {
  const desconhecidas = presentes.filter((chave) => !CHAVES_ATOMICAS.includes(chave))
  if (desconhecidas.length > 0) {
    erros.push(
      erro(
        caminho,
        'ESTRUTURA_INVALIDA',
        `Chave(s) não reconhecida(s) na condição: ${desconhecidas.join(', ')}`
      )
    )
    return
  }
  for (const obrigatoria of ['fonte', 'ref', 'op']) {
    if (!presentes.includes(obrigatoria)) {
      erros.push(
        erro(caminho, 'ESTRUTURA_INVALIDA', `Condição sem a chave obrigatória "${obrigatoria}"`)
      )
      return
    }
  }

  const fonteBruta = typeof no.fonte === 'string' ? (no.fonte as string) : null
  const fonte = FONTES.find((f) => f === fonteBruta)
  if (!fonte) {
    erros.push(
      erro(`${caminho}.fonte`, 'FONTE_DESCONHECIDA', `Fonte desconhecida: ${descreve(no.fonte)}`)
    )
    return
  }
  if (!esquema.fontesPermitidas.includes(fonte)) {
    erros.push(
      erro(`${caminho}.fonte`, 'FONTE_NAO_PERMITIDA', `Fonte "${fonte}" não é permitida neste uso`)
    )
    return
  }

  const opBruto = typeof no.op === 'string' ? (no.op as string) : null
  const operador = OPERADORES.find((o) => o === opBruto)
  if (!operador) {
    erros.push(
      erro(
        `${caminho}.op`,
        'OPERADOR_DESCONHECIDO',
        `Operador desconhecido: ${descreve(no.op)}. Use ${OPERADORES.join(', ')}`
      )
    )
    return
  }

  const ref = typeof no.ref === 'string' ? (no.ref as string) : null
  if (ref == null || ref.trim() === '') {
    erros.push(erro(`${caminho}.ref`, 'ESTRUTURA_INVALIDA', 'Referência da condição é obrigatória'))
    return
  }

  const referencia = resolverReferencia(fonte, ref, caminho, esquema, erros)
  if (!referencia) return

  const aceitos = operadoresPara(esquema.catalogo, referencia.tipo, referencia.lista)
  if (!aceitos.some((o) => o.json === operador)) {
    erros.push(
      erro(
        `${caminho}.op`,
        'OPERADOR_INCOMPATIVEL',
        `Operador "${operador}" não se aplica a "${ref}"`
      )
    )
    return
  }
  validarValor(no, caminho, operador, referencia, ref, erros, presentes)
}

const resolverReferencia = (
  fonte: Fonte,
  ref: string,
  caminho: string,
  esquema: EsquemaValidacao,
  erros: ErroCondicao[]
): Referencia | null => {
  if (fonte === 'campo') {
    const campo = esquema.campos.find((c) => c.codigo === ref)
    if (!campo) {
      erros.push(
        erro(`${caminho}.ref`, 'CAMPO_INEXISTENTE', `Campo "${ref}" não existe no formulário`)
      )
      return null
    }
    const tipoValor = tipoValorDoCampo(campo.tipo)
    if (!tipoValor) {
      erros.push(
        erro(
          `${caminho}.ref`,
          'CAMPO_TIPO_NAO_SUPORTADO',
          `Campo "${ref}" é de um tipo que não pode ser usado em condição`
        )
      )
      return null
    }
    const atual = esquema.campoAtual
    if (atual && !antesDe(campo, atual)) {
      erros.push(
        erro(
          `${caminho}.ref`,
          'CAMPO_POSTERIOR',
          `Campo "${ref}" precisa vir antes de "${atual.codigo}"`
        )
      )
      return null
    }
    return {
      tipo: tipoValor,
      lista: campo.multiplo,
      dominio: campo.tipo === 'OPCAO' ? campo.codigosOpcao : [],
    }
  }
  if (fonte === 'variavel') {
    const variavel = variavelPorCodigo(esquema.catalogo, ref)
    if (!variavel) {
      erros.push(
        erro(`${caminho}.ref`, 'VARIAVEL_DESCONHECIDA', `Variável "${ref}" não está no catálogo`)
      )
      return null
    }
    return {
      tipo: variavel.tipo,
      lista: false,
      dominio: variavel.valoresPermitidos ? variavel?.valoresPermitidos?.map((v) => v.valor) : [],
    }
  }
  if (!sinalPorCodigo(esquema.catalogo, ref)) {
    erros.push(erro(`${caminho}.ref`, 'SINAL_DESCONHECIDO', `Sinal "${ref}" não está no catálogo`))
    return null
  }
  return { tipo: 'NUMERO', lista: false, dominio: [] }
}

const validarValor = (
  no: Record<string, unknown>,
  caminho: string,
  op: Operador,
  referencia: Referencia,
  ref: string,
  erros: ErroCondicao[],
  presentes: string[]
): void => {
  const caminhoValor = `${caminho}.valor`
  const valor = no.valor

  if (!operadorExigeValor(op)) {
    if (presentes.includes('valor')) {
      erros.push(
        erro(caminhoValor, 'ESTRUTURA_INVALIDA', `Operador "${op}" não aceita a chave "valor"`)
      )
    }
    return
  }
  if (valor === undefined || valor === null) {
    erros.push(erro(caminhoValor, 'ESTRUTURA_INVALIDA', `Operador "${op}" exige a chave "valor"`))
    return
  }
  if (op === 'entre') {
    validarIntervalo(valor, caminhoValor, erros)
    return
  }
  if (op === 'em') {
    validarLista(valor, caminhoValor, referencia, ref, erros)
    return
  }
  validarEscalar(valor, caminhoValor, referencia, ref, erros)
}

const validarIntervalo = (valor: unknown, caminhoValor: string, erros: ErroCondicao[]): void => {
  if (
    !Array.isArray(valor) ||
    valor.length !== 2 ||
    typeof valor[0] !== 'number' ||
    typeof valor[1] !== 'number'
  ) {
    erros.push(
      erro(
        caminhoValor,
        'INTERVALO_INVALIDO',
        'Operador "entre" exige um intervalo [a, b] com dois números'
      )
    )
    return
  }
  if (valor[0] > valor[1]) {
    erros.push(
      erro(
        caminhoValor,
        'INTERVALO_INVALIDO',
        'No intervalo [a, b] o início não pode ser maior que o fim'
      )
    )
  }
}

const validarLista = (
  valor: unknown,
  caminhoValor: string,
  referencia: Referencia,
  ref: string,
  erros: ErroCondicao[]
): void => {
  if (!Array.isArray(valor) || valor.length === 0) {
    erros.push(
      erro(caminhoValor, 'TIPO_VALOR_INCOMPATIVEL', 'Operador "em" exige uma lista não vazia')
    )
    return
  }
  valor.forEach((item, indice) =>
    validarEscalar(item, `${caminhoValor}[${indice}]`, referencia, ref, erros)
  )
}

const validarEscalar = (
  valor: unknown,
  caminhoValor: string,
  referencia: Referencia,
  ref: string,
  erros: ErroCondicao[]
): void => {
  if (referencia.tipo === 'NUMERO') {
    if (typeof valor !== 'number') {
      erros.push(
        erro(
          caminhoValor,
          'TIPO_VALOR_INCOMPATIVEL',
          `"${ref}" é numérico; o valor ${descreve(valor)} não é um número`
        )
      )
    }
    return
  }
  if (typeof valor !== 'string') {
    erros.push(
      erro(
        caminhoValor,
        'TIPO_VALOR_INCOMPATIVEL',
        `"${ref}" é textual; o valor ${descreve(valor)} não é um texto`
      )
    )
    return
  }
  if (referencia.dominio.length > 0) {
    const normalizado = normalizar(valor)
    if (!referencia.dominio.map(normalizar).includes(normalizado)) {
      erros.push(
        erro(
          caminhoValor,
          'VALOR_FORA_DO_DOMINIO',
          `Valor "${valor}" não é um dos valores de "${ref}": ${referencia.dominio.join(', ')}`
        )
      )
    }
  }
}
